import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import FormErrors from "@/components/FormErrors/FormErrors.vue";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import { v4 as uuidv4 } from "uuid";
import { JSConn } from "@/utilities/js-conn";

import { TOAST_OPTIONS } from "@/constants";
import { NamedRts } from "@/router";
import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";

import { buildFormLotUnit, prettifyLotUnitStatus } from "@/models/LotUnit";
import { LotUnit } from "@/store/modules/lotUnits";

export default defineComponent({
  name: "lotUnitEditForm",
  components: {
    FormErrors,
    LoadingAnimation,
  },
  props: {
    lotUnitId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    let dataLoaded = ref(false);
    let loadingMessage = ref("Preparing the form...");

    let firstInput: any = ref(null);
    let deleteModal: any = ref(null);

    const showDeleteModal = () => {
      deleteModal.value.show();
    };

    const hideDeleteModal = () => {
      deleteModal.value.hide();
    };

    const deleting = ref(false);

    const deleteLotUnit = () => {
      deleting.value = true;
      const jsConn = new JSConn();
      jsConn.formParams = {
        id: props.lotUnitId,
      };
      $store.dispatch("deleteLotUnit", jsConn).then(jsConn => {
        deleting.value = false;
        ctx.root.$router.push(cancelParams());
        return Promise.resolve(jsConn);
      });
    };

    const cancelParams = () => {
      if (props.lotUnitId === "new") {
        return {
          name: NamedRts.lotUnits,
        };
      } else {
        return {
          name: NamedRts.lotUnits,
        };
      }
    };

    let {
      loadLotUnitIntoForm,
      formLotUnit,
      lotUnitStatuses,
      loadLotUnitStatusesIntoForm,
    } = buildFormLotUnit();

    const apiLotUnit = computed(() => {
      return $store.getters.lotUnit as LotUnit;
    });

    onMounted(() => {
      let dataPromises = [];
      let jsConn = new JSConn();
      loadLotUnitStatusesIntoForm(lotUnitStatuses);

      if (props.lotUnitId !== "new") {
        jsConn.formParams = {
          id: props.lotUnitId,
        };

        dataPromises.push($store.dispatch("needLotUnit", jsConn));
      }

      Promise.all(dataPromises)
        .then(() => {
          if (props.lotUnitId !== "new") {
            loadLotUnitIntoForm(apiLotUnit.value, formLotUnit);
          }
        })
        .finally(() => {
          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        });
    });

    let { submitButtonText, submitting } = buildSubmitButton();
    let { hasFormErrors, formErrors } = buildFormErrors();

    const onSubmit = (evt: any) => {
      evt.preventDefault();
      const jsConn = new JSConn();

      hasFormErrors.value = false;
      formErrors.value = [];
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      let storeDispatch;

      if (props.lotUnitId === "new") {
        formLotUnit.id = uuidv4();
        jsConn.formParams = formLotUnit;
        storeDispatch = "createLotUnit";
      } else {
        jsConn.formParams = formLotUnit;
        storeDispatch = "updateLotUnit";
      }

      $store
        .dispatch(storeDispatch, jsConn)
        .then(jsConn => {
          ctx.root.$bvToast.toast(
            "Lot Unit was successfully saved",
            TOAST_OPTIONS,
          );
          ctx.root.$router.push(cancelParams());
          return Promise.resolve(jsConn);
        })
        .catch(jsConn => {
          hasFormErrors.value = true;
          formErrors.value = jsConn.error["response"]["data"];
        });
    };

    return {
      dataLoaded,
      loadingMessage,
      firstInput,
      cancelParams,

      formLotUnit,

      onSubmit,
      submitButtonText,
      submitting,
      hasFormErrors,
      formErrors,

      lotUnitStatuses,
      prettifyLotUnitStatus,

      deleteModal,
      showDeleteModal,
      hideDeleteModal,
      deleting,
      deleteLotUnit,
    };
  },
});
